'use client';

import { useState } from 'react';

import {
  OtpVerificationModal,
  type OtpVerificationModalProps,
} from '@/components/OtpVerificationModal';
import { Button } from '@/components/ui';
import { AnimatePresence, motion } from '@/libs';

import {
  RecoveryCpfForm,
  RecoveryError,
  RecoveryKycVerification,
  RecoveryPhoneForm,
  RecoverySuccess,
  RecoveryValidating,
  UserAddressForm,
  UserLoginForm,
  UserProfile,
  UserRegisterForm,
} from './components';
import { RECOVERY_MESSAGES } from './constants';
import { useUserDrawer } from './hooks';

export const UserDrawer = () => {
  const [recoveryOtpVerification, setRecoveryOtpVerification] =
    useState<OtpVerificationModalProps | null>(null);

  const {
    currentStep,
    userData,
    registrationSeed,
    recoveryCustomerData,
    recoveryErrorMessage,
    handleLoginSuccess,
    handleNeedsRegistration,
    handleRegisterSuccess,
    handleAddressComplete,
    handleSkipAddress,
    handleCancelRegistration,
    handleLogout,
    handleStartRecovery,
    handleRecoveryCpfSuccess,
    handleKycStartValidation,
    handleKycSuccess,
    handleKycError,
    handlePhoneUpdateSuccess,
    handleRetryKyc,
    handleBackToLogin,
    handleEditProfile,
    handleMyTickets,
  } = useUserDrawer();

  const renderStep = () => {
    switch (currentStep) {
      case 'register':
        return registrationSeed ? (
          <UserRegisterForm
            seed={registrationSeed}
            onRegisterSuccess={handleRegisterSuccess}
            onBack={handleCancelRegistration}
          />
        ) : (
          <UserLoginForm
            onLoginSuccess={handleLoginSuccess}
            onNeedsRegistration={handleNeedsRegistration}
            onStartRecovery={handleStartRecovery}
          />
        );
      case 'address':
        return (
          <UserAddressForm
            onAddressComplete={handleAddressComplete}
            onBack={handleSkipAddress}
          />
        );
      case 'profile':
        return (
          <UserProfile
            onEditProfile={handleEditProfile}
            onMyTickets={handleMyTickets}
            userData={userData!}
            onLogout={handleLogout}
          />
        );
      case 'recovery-cpf':
        return (
          <RecoveryCpfForm
            onSubmit={handleRecoveryCpfSuccess}
            onBackToLogin={handleBackToLogin}
          />
        );
      case 'recovery-kyc':
        return recoveryCustomerData?.customerId ? (
          <RecoveryKycVerification
            customerId={recoveryCustomerData.customerId}
            onStartValidation={handleKycStartValidation}
            onBackToLogin={handleBackToLogin}
          />
        ) : (
          <RecoveryError
            onRetry={handleStartRecovery}
            onBackToLogin={handleBackToLogin}
          />
        );
      case 'recovery-validating':
        return recoveryCustomerData?.customerId ? (
          <RecoveryValidating
            customerId={recoveryCustomerData.customerId}
            onValidationSuccess={handleKycSuccess}
            onValidationError={() => handleKycError(RECOVERY_MESSAGES.KYC_FAILED)}
            onValidationTimeout={() => handleKycError(RECOVERY_MESSAGES.KYC_TIMEOUT)}
          />
        ) : (
          <RecoveryError
            onRetry={handleStartRecovery}
            onBackToLogin={handleBackToLogin}
          />
        );
      case 'recovery-phone':
        return recoveryCustomerData ? (
          <RecoveryPhoneForm
            cpf={recoveryCustomerData.cpf}
            onSuccess={handlePhoneUpdateSuccess}
            onOtpVerificationChange={setRecoveryOtpVerification}
          />
        ) : (
          <RecoveryError
            onRetry={handleStartRecovery}
            onBackToLogin={handleBackToLogin}
          />
        );
      case 'recovery-success':
        return <RecoverySuccess onBackToLogin={handleBackToLogin} />;
      case 'recovery-error':
        return (
          <RecoveryError
            message={recoveryErrorMessage}
            onRetry={handleRetryKyc}
            onBackToLogin={handleBackToLogin}
          />
        );
      case 'login':
      default:
        return (
          <UserLoginForm
            onLoginSuccess={handleLoginSuccess}
            onNeedsRegistration={handleNeedsRegistration}
            onStartRecovery={handleStartRecovery}
          />
        );
    }
  };

  const isLoginStep = currentStep === 'login';
  const isProfileStep = currentStep === 'profile';

  return (
    <div data-cy="user-drawer" className="flex flex-grow flex-col gap-3 p-4">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          data-cy={
            isLoginStep
              ? 'user-drawer-login'
              : isProfileStep
                ? 'user-drawer-profile'
                : `user-drawer-${currentStep}`
          }
          initial={{ opacity: 0, x: isProfileStep ? 20 : -20, scale: 0.95 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: isProfileStep ? -20 : 20, scale: 0.95 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
          {renderStep()}
        </motion.div>
      </AnimatePresence>

      {recoveryOtpVerification && (
        <div
          data-cy="recovery-phone-otp-modal"
          className="fixed bottom-0 right-0 z-[2100] flex h-screen w-screen items-center justify-center bg-black/80 p-4"
          role="dialog"
          aria-modal="true"
          onClick={recoveryOtpVerification.onClose}
        >
          <div className="relative w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <Button
              data-cy="recovery-phone-otp-close-button"
              icon="Close"
              variant="ghost"
              size="small"
              className="absolute right-3 top-3 z-20 modal-close-button sm:hover:bg-foreground/0"
              aria-label="fechar modal"
              onClick={recoveryOtpVerification.onClose}
            />
            <OtpVerificationModal {...recoveryOtpVerification} />
          </div>
        </div>
      )}
    </div>
  );
};
